/* ============================================================================
   HeroDexButton — b17m (rebased on b17i+b17n main).

   Builds on b17i (128px hero coin, idle halo pulse, coachmark, radial menu)
   and b17n (variant fix).

   Changes in b17m:
     A. Asset swap: import nexus_dex_medallion_3d.png instead of
        nexus_dex_logo_mark.png. The new image is the full medallion (silver
        bezel + green inner + N logo all baked in as a single transparent PNG).
     B. SVG bezel/disc layers removed: compass-coin__face, __ring-inner, and
        __disc wrappers are dropped. Only the halo (__glow) and the medallion
        <img> remain inside compass-coin.
     C. Position down 30px: was -mt-8 (marginTop -32px), now marginTop -2px.
        Medallion still lifts above the tab bar but 30px lower than b17i.
     D. QuickActionMenu replaces RadialMenu: flat slim panel above the
        medallion with a single "LIVE SCAN" chip. On tap calls quickStart()
        from useDexLiveScanQuickStart. ~25% shorter vertical footprint vs the
        prior radial menu arc height.
     E. useDexLiveScanQuickStart: new hook — starts the live scan immediately
        from the menu, no DexLiveScanMenu modal pop.

   Changes in b17q:
     - Q2: the .compass-coin__glow halo is removed entirely (it read as a pale
       green ring over the lighter background once the medallion sat lower).
       The tap-pulse state that only drove the halo animation is gone too.
     - Q3: the emblem <img> fills the 128px coin edge-to-edge (was 112px, which
       exposed the chrome ring around the medallion).

   Preserved from b17i:
     - First-launch coachmark ("Long-press for quick actions", auto-dismiss 4s)
     - iOS guards: draggable={false} + WebkitTouchCallout:"none" on emblem img

   iOS guards (from b17h) preserved:
     - draggable={false} + WebkitTouchCallout:"none" on the emblem <img>
     - .compass-coin__emblem CSS: -webkit-user-drag:none + callout:none

   All colour comes from theme tokens. No raw hex in rendered markup.
   ============================================================================ */
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "wouter";
import { Crown } from "lucide-react";
import { hapticMedium, hapticTick } from "@/lib/haptics";
import nexusDexMedallion from "@/assets/nexus_dex_medallion_3d.png";
import { useDexLiveScanQuickStart } from "@/hooks/useDexLiveScanQuickStart";
import { HERO_TOP_OFFSET_PX, COIN_DIAMETER_PX, HERO_WRAPPER_HEIGHT_PX } from "./tabbarLayout";
import { INBOX_OPEN_EVENT } from "./AppShell";
import { confirmPageExit } from "@/lib/pageExitGuard";
import { useSession, hasProAccess, tierLabel } from "./SessionProvider";
import { useFirstRunPromptDue } from "@/lib/useFirstRunPrompt";
import { claimFirstRunPrompt, markFirstRunPromptDone } from "@/lib/firstRunPrompts";

interface HeroDexButtonProps {
  href: string;
  active: boolean;
}

const LONG_PRESS_MS = 450;

// ---------------------------------------------------------------------------
// MedallionCoachmark
// Renders a small tooltip above the medallion. Auto-dismisses after 4s or on
// tap. Its turn and its once-per-device record are lib/firstRunPrompts'.
// ---------------------------------------------------------------------------
interface CoachmarkProps {
  onDismiss: () => void;
}

function MedallionCoachmark({ onDismiss }: CoachmarkProps) {
  useEffect(() => {
    const t = setTimeout(onDismiss, 4000);
    return () => clearTimeout(t);
  }, [onDismiss]);

  return (
    <button
      type="button"
      data-testid="medallion-coachmark"
      onClick={onDismiss}
      aria-label="Dismiss tip"
      className="absolute z-50 pointer-events-auto"
      style={{
        bottom: "calc(100% + 14px)",
        left: "50%",
        transform: "translateX(-50%)",
        background: "none",
        border: "none",
        padding: 0,
        cursor: "pointer",
      }}
    >
      <span
        style={{
          display: "block",
          whiteSpace: "nowrap",
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: "0.04em",
          color: "var(--text-primary)",
          background: "var(--surface-raised)",
          border: "1px solid var(--border-subtle)",
          boxShadow: "var(--shadow-lg)",
          borderRadius: "var(--radius-md)",
          padding: "6px 12px",
          pointerEvents: "auto",
        }}
      >
        Long-press for quick actions
        {/* small downward caret */}
        <span
          aria-hidden
          style={{
            position: "absolute",
            bottom: -6,
            left: "50%",
            transform: "translateX(-50%)",
            width: 0,
            height: 0,
            borderLeft: "6px solid transparent",
            borderRight: "6px solid transparent",
            borderTop: "6px solid var(--border-subtle)",
          }}
        />
      </span>
    </button>
  );
}

// ---------------------------------------------------------------------------
// QuickActionMenu — PR-E8 fix 6
//
// Wifi-arc layout above the medallion: three nested arcs whose curved labels
// (LIVE SCAN → NOTIFICATIONS → SETTINGS) form a stylised wifi-signal stack,
// crowned by a tier pill ("Pro" / "Basic") at the top. Each arc is a tappable
// menu item so the gesture footprint stays inside the same area the medallion
// would otherwise occupy on tap-up.
//
// Geometry:
//   - SVG canvas 280×140, positioned so its bottom edge sits ~6px above the
//     medallion. The arcs share a common centre at (140, 140) i.e. just below
//     the canvas, so each arc opens upward like the wifi glyph.
//   - Three radii: r1=58 (LIVE SCAN, innermost), r2=84 (NOTIFICATIONS, mid),
//     r3=110 (SETTINGS, outermost). Each arc subtends ~140° so the labels
//     curve evenly without the endpoints touching the canvas walls.
//   - The tier pill sits above the outermost arc at the canvas top.
//
// Everything routes through theme tokens — no raw hex. Brand neon-green is
// strictly forbidden outside ProLock.tsx, so the tier pill uses
// var(--accent-fill) (token-driven brand colour).
// ---------------------------------------------------------------------------
interface QuickActionMenuProps {
  onClose: () => void;
  onLiveScan: () => void;
  onNotifications: () => void;
  onSettings: () => void;
  onWishList: () => void;
  tierName: string;
  isPro: boolean;
}

// Geometry constants — derived once so the SVG and the tap targets stay in
// lockstep if we ever resize the menu.
//
// PR-E9 redesign: the menu now reads top-to-bottom as LIVE SCAN -> NOTIFICATIONS
// -> SETTINGS, matching IMG_0534. Because arcs are drawn from a common centre
// at the bottom of the canvas, the LARGEST radius renders highest on screen.
//
// PR-E11 fix 5: the previous radii (50/74/98) left only the stroke width
// between adjacent rings, so the three labels visibly overlapped on iPhone.
// Bumped to 56/86/116 so each ring sits ~6px clear of its neighbour (the
// stroke is 12px wide either side of the path; 30px ring-to-ring centres
// minus 24px combined stroke width = 6px visible gap).
//
// PR-E12 fix 2: label-to-radius mapping reordered so the longest label sits
// on the biggest arc. NOTIFICATIONS (longest) now owns R_OUTER, SETTINGS owns
// R_MID, LIVE SCAN (shortest, primary action) owns R_INNER closest to the
// medallion. The accent-tint follows LIVE SCAN to the inner ring; the two
// neutral rings (now outer + mid) both use a translucent fill so the page
// reads through them. Sweep tightened from 110 deg -> 77 deg (70% per spec)
// so the arcs read as a tighter wifi signal centred over the medallion.
const MENU_W = 280;
// E51: with the bands stepped out by ARC_BASE_RADIUS = ARC_BAND_GAP, the outer
// band radius grows 116 -> 120. The outermost band's top edge sits at
// ARC_CY - ARC_R_OUTER - STRIP = 132 - 120 - 12 = 0 in canvas space, i.e. flush
// with the top of the MENU_H=132 canvas. The SVG is rendered with
// overflow:visible inside a container that already reserves MENU_H + 32 of
// height, so the band is not clipped and MENU_H does not need to grow.
const MENU_H = 132;
const ARC_CX = 140;
const ARC_CY = 132; // common centre at the bottom of the canvas

// E45/E51: the three bands are derived from a single spacing rhythm so the gap
// between the coin and the innermost LIVE SCAN band equals the gap between
// consecutive bands. ARC_BAND_GAP is the radial spacing between adjacent band
// centrelines; ARC_BASE_RADIUS is the coin-edge anchor at the arc centre. Each
// band radius is ARC_BASE_RADIUS plus an integer multiple of ARC_BAND_GAP.
//
// E51: ARC_BASE_RADIUS is set EQUAL to ARC_BAND_GAP. The coin-edge -> inner step
// is (ARC_R_INNER - ARC_BASE_RADIUS) = ARC_BAND_GAP, and the coin anchor itself
// is ARC_BASE_RADIUS = ARC_BAND_GAP, so the coin -> inner distance now equals the
// inner -> middle inter-band gap (both ARC_BAND_GAP). This supersedes the E45
// note that claimed they were already equal: base was 26 while the gap was 30,
// so LIVE SCAN actually sat closer to the coin (26) than to SETTINGS (30).
// Setting base = gap makes the whole stack a clean arithmetic progression
// stepping by ARC_BAND_GAP from the coin edge. Exported so the layout test can
// assert the even-spacing contract.
export const ARC_BAND_GAP = 30;
export const ARC_BASE_RADIUS = ARC_BAND_GAP; // E51: coin anchor == inter-band gap (was 26)
// Geometric anchors (do not rename). Label-to-radius mapping is PR-E12:
//   LIVE SCAN     -> ARC_R_INNER (innermost, accent tint)
//   SETTINGS      -> ARC_R_MID   (middle)
//   NOTIFICATIONS -> ARC_R_OUTER (outermost, longest label)
export const ARC_R_INNER = ARC_BASE_RADIUS + ARC_BAND_GAP;     // E51: 60 (was 56)
export const ARC_R_MID   = ARC_BASE_RADIUS + ARC_BAND_GAP * 2; // E51: 90 (was 86)
export const ARC_R_OUTER = ARC_BASE_RADIUS + ARC_BAND_GAP * 3; // E51: 120 (was 116)
// wish-list-screen: a FOURTH band added ABOVE the existing three. Larger radius
// renders higher on screen, so the Wish List band is the outermost/topmost one,
// stepping out by the same ARC_BAND_GAP rhythm as the rest of the stack.
export const ARC_R_WISHLIST = ARC_BASE_RADIUS + ARC_BAND_GAP * 4; // 150
const ARC_SWEEP_DEG = 77;

// E54: half-thickness of each band's rounded stroke (the band is STRIP px wide
// on either side of its centreline path, so the full stroke is STRIP * 2). The
// band-to-band rhythm and the coin-to-inner spacing are both measured between
// VISIBLE edges, and an edge sits STRIP from its centreline — so STRIP has to be
// a module-level constant shared by both the SVG draw (strokeWidth) and the
// offset derivation below, not a local inside QuickActionMenu. Exported so the
// layout test can assert the visible-gap contract.
export const ARC_STRIP = 12;

// wish-list-screen: the outermost Wish List band's top edge sits at
// ARC_CY - ARC_R_WISHLIST - ARC_STRIP = 132 - 150 - 12 = -30 in canvas space,
// i.e. 30px above the MENU_H=132 canvas top. We push the SVG down (and grow the
// container height) by this overhang so the band is never clipped and the tier
// pill still caps the stack above it. Derived, not a bare literal.
export const ARC_WISHLIST_OVERHANG = Math.max(0, ARC_R_WISHLIST + ARC_STRIP - ARC_CY); // 30

// E54: drop the whole arc stack DOWN so the ON-SCREEN VISIBLE gap from the coin
// to the inner LIVE SCAN band equals the VISIBLE band-to-band gap. E51 made the
// RADII equidistant and E53 equalized the coin-to-inner CENTRELINE gap with the
// band-to-band centreline gap (both ARC_BAND_GAP) — but the user still saw the
// inner gap as visibly larger. The reason: the gaps the eye reads are between
// VISIBLE EDGES, not centrelines, and a band edge sits ARC_STRIP from its
// centreline. Two adjacent bands each contribute one ARC_STRIP, so the visible
// band-to-band gap is (ARC_BAND_GAP - 2 * ARC_STRIP). But the coin has no stroke,
// so the coin-to-inner visible gap only loses ONE ARC_STRIP (the inner band's),
// giving (ARC_BAND_GAP - ARC_STRIP) — one ARC_STRIP (12px) too wide. That extra
// 12px is exactly the gap the user reported.
//
// Fix: target the coin-to-inner CENTRELINE gap at (ARC_BAND_GAP - ARC_STRIP) so
// that after the single inner-band half-strip is subtracted, the VISIBLE coin-to
// -inner gap equals (ARC_BAND_GAP - 2 * ARC_STRIP) — identical to the visible
// band-to-band gap. The band radii (band-to-band rhythm) are untouched; only the
// stack's vertical anchor drops a further ARC_STRIP.
//
// On-screen vertical model (down positive, origin = hero wrapper top edge):
//   - The coin is COIN_DIAMETER_PX tall, bottom-aligned in a wrapper only
//     HERO_WRAPPER_HEIGHT_PX tall, so the coin TOP edge sits
//     (COIN_DIAMETER_PX - HERO_WRAPPER_HEIGHT_PX) above the wrapper top.
//   - The menu container's bottom is ARC_MENU_BOTTOM_OFFSET_PX above the wrapper
//     top (CSS `bottom: calc(100% + offset)`); MENU_H === ARC_CY, so the SVG arc
//     centre lands exactly on the container bottom, and the inner band centreline
//     sits ARC_R_INNER above it.
//   - Therefore coinTop -> inner-band centreline =
//       ARC_MENU_BOTTOM_OFFSET_PX + ARC_R_INNER - (COIN_DIAMETER_PX - HERO_WRAPPER_HEIGHT_PX).
//   Setting that equal to (ARC_BAND_GAP - ARC_STRIP) and solving for the offset
//   gives the constant below. With the current geometry it evaluates to -2px,
//   i.e. the stack drops a further ARC_STRIP (12px) from E53's 10px so the
//   VISIBLE coin-to-inner gap matches the VISIBLE band-to-band gap. Derived from
//   coin + band geometry, NOT a bare literal; comment E54.
export const ARC_MENU_BOTTOM_OFFSET_PX =
  (ARC_BAND_GAP - ARC_STRIP) -
  (ARC_R_INNER - (COIN_DIAMETER_PX - HERO_WRAPPER_HEIGHT_PX)); // -2

// E53: all three bands share one NEARLY-SOLID MINT GREEN fill, drawn from the
// existing accent family (--accent-fill is #0DA371 = rgb(13,163,113)). Alpha is
// 0.90 ("barely transparent / not see-through"), superseding E51's 0.18: at 18%
// the mint washed out and the white label text did not read clearly, so the user
// asked for a near-solid mint. A trace of transparency (0.90, not 1.0) keeps the
// chip composited over BOTH the light and dark themes the menu can open over and
// avoids a harsh flat block. All three bands consume this one constant; LIVE SCAN
// now reads as the primary action via the GREEN word "LIVE" in its label (E53),
// not via a different fill or a different outline. The drop-shadow is unchanged.
export const ARC_BAND_FILL = "rgba(13, 163, 113, 0.90)";

// E51: downward font-metric correction applied to each curved label's textPath
// so the glyph optical centre lands on the band centreline. The label font size
// is 11px; on engines that resolve a textPath to the alphabetic baseline (rather
// than honouring central baseline) the glyphs sit high, floating toward the
// outer band edge. Expressed in em so it scales with the label font size.
//
// E63: bumped 0.32em → 0.44em. On device the labels still read as top-aligned
// (hugging the outer edge of each band) rather than sitting in the middle. The
// caps are ~0.72em tall, so their optical centre is ~0.36em above the alphabetic
// baseline; nudging the baseline down by ~0.44em lands the cap midline on the
// band centreline, giving equal band above and below each label. Verified in a
// standalone arc render against the band centreline (0.40 still high, 0.48 dips
// low, 0.44 centred).
//
// E54: the plain-text labels (NOTIFICATIONS, SETTINGS) carry this dy on their
// <textPath>; the split-colour LIVE SCAN label carries it on its first <tspan>
// instead, because WebKit does not propagate a <textPath> dy to glyphs inside
// nested tspans. dy is cumulative along the run, so placing it on the first
// tspan shifts the whole "Live Scan" run down uniformly (the same amount as the
// other two labels) — it must NOT be repeated on the second tspan or the words
// would step apart vertically.
const LABEL_BASELINE_DY = "0.44em";

// Build an SVG arc path for textPath. Sweeps the top of a circle so the text
// reads left-to-right along the upper arc. Angles measured clockwise from
// 12 o'clock (−0.5π in SVG y-flipped space), centred on top.
function arcPath(cx: number, cy: number, r: number, sweepDeg: number): string {
  const half = (sweepDeg * Math.PI) / 360;
  // Start angle: −π/2 − half; end angle: −π/2 + half.
  const startA = -Math.PI / 2 - half;
  const endA = -Math.PI / 2 + half;
  const x1 = cx + r * Math.cos(startA);
  const y1 = cy + r * Math.sin(startA);
  const x2 = cx + r * Math.cos(endA);
  const y2 = cy + r * Math.sin(endA);
  const largeArc = sweepDeg > 180 ? 1 : 0;
  // sweep-flag 1 draws clockwise so the textPath reads top-side outwards.
  return `M ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2}`;
}

function QuickActionMenu({
  onClose,
  onLiveScan,
  onNotifications,
  onSettings,
  onWishList,
  tierName,
  isPro,
}: QuickActionMenuProps) {
  // Tap-target arc thickness on either side of each label arc. Module-level
  // ARC_STRIP so the band-edge geometry is shared with the offset derivation.
  const STRIP = ARC_STRIP;

  return (
    <>
      {/* Tap-away scrim. Closes the menu on any background tap. */}
      <button
        type="button"
        aria-label="Close quick actions"
        data-testid="radial-menu-scrim"
        onClick={onClose}
        className="fixed inset-0 z-40"
        style={{ background: "transparent", border: "none" }}
      />
      {/* Wifi-arc panel — positioned ABOVE the medallion. The container sits
          flush against the medallion's top edge; the SVG canvas opens upward
          so the bottommost arc is closest to the coin. */}
      <div
        role="menu"
        data-testid="hero-dex-panel"
        aria-label="Quick actions"
        className="absolute z-50"
        style={{
          // PR-E12 fix 2 bumped this to `calc(100% + 24px)` for breathing room.
          // E53: lowered to ARC_MENU_BOTTOM_OFFSET_PX (derived, ~10px) to drop the
          // whole arc stack so the coin -> inner-band centreline gap equals
          // ARC_BAND_GAP on screen (see the constant's derivation above). The
          // bands keep their relative ARC_BAND_GAP spacing; only the stack's
          // vertical anchor moves closer to the coin.
          bottom: `calc(100% + ${ARC_MENU_BOTTOM_OFFSET_PX}px)`,
          left: "50%",
          transform: "translateX(-50%)",
          width: MENU_W,
          // +32 reserves space for the tier pill above arcs; the extra overhang
          // reserves room for the outermost Wish List band (wish-list-screen).
          height: MENU_H + 32 + ARC_WISHLIST_OVERHANG,
          pointerEvents: "none",
        }}
      >
        {/* Tier pill: "Pro" or "Basic" depending on account tier. Sits at the
            top of the menu so it caps the wifi-arc stack like a status badge. */}
        <div
          style={{
            position: "absolute",
            top: 0,
            left: "50%",
            transform: "translateX(-50%)",
            pointerEvents: "none",
          }}
          data-testid="hero-dex-tier-pill"
        >
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              padding: "4px 10px",
              borderRadius: "var(--radius-pill)",
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: "0.04em",
              // Pro = filled brand-accent badge; Basic = quiet outline so the
              // free-tier user reads as muted rather than rejected.
              background: isPro
                ? "var(--accent-fill)"
                : "var(--surface-raised)",
              color: isPro
                ? "var(--text-on-accent)"
                : "var(--text-secondary)",
              border: isPro
                ? "1px solid var(--accent-fill)"
                : "1px solid var(--border-subtle)",
              boxShadow: "var(--shadow-sm)",
            }}
          >
            <Crown
              className="w-3 h-3"
              style={{
                color: isPro
                  ? "var(--text-on-accent)"
                  : "var(--text-tertiary)",
              }}
            />
            {tierName}
          </span>
        </div>

        {/* SVG canvas containing the three label arcs + invisible tap-target
            arcs. The SVG itself is pointer-events: none; only the per-item
            buttons (rendered on top) capture taps. */}
        <svg
          width={MENU_W}
          height={MENU_H}
          viewBox={`0 0 ${MENU_W} ${MENU_H}`}
          style={{
            position: "absolute",
            // Push the canvas down by the tier-pill reserve (32) plus the Wish
            // List band's overhang so the outermost band clears the pill and is
            // never clipped at the container top (wish-list-screen).
            top: 32 + ARC_WISHLIST_OVERHANG,
            left: 0,
            pointerEvents: "none",
            overflow: "visible",
          }}
          aria-hidden
        >
          {/* Path definitions for the three label arcs. Defs so the textPath
              hrefs resolve cleanly.

              PR-E12 fix 2 (reordered): longest label NOTIFICATIONS now sits on
              the OUTERMOST arc, SETTINGS in the middle, LIVE SCAN closest to
              the medallion. */}
          <defs>
            <path id="hero-arc-wishlist" d={arcPath(ARC_CX, ARC_CY, ARC_R_WISHLIST, ARC_SWEEP_DEG)} />
            <path id="hero-arc-notifs" d={arcPath(ARC_CX, ARC_CY, ARC_R_OUTER, ARC_SWEEP_DEG)} />
            <path id="hero-arc-settings" d={arcPath(ARC_CX, ARC_CY, ARC_R_MID, ARC_SWEEP_DEG)} />
            <path id="hero-arc-livescan" d={arcPath(ARC_CX, ARC_CY, ARC_R_INNER, ARC_SWEEP_DEG)} />
            {/* E62: 3D depth via colour contrast, NOT a centreline. A vertical
                mint gradient (bright at the top edge of the stack → deep mint at
                the bottom) lights each band's stroke from above like a raised,
                curved surface. userSpaceOnUse in canvas coords: the outermost
                band apex sits near y=-18 and the innermost near y=72, so the
                span -32→104 covers the whole stack with headroom. The literal
                RGB stays fixed across light/dark themes to match ARC_BAND_FILL,
                which is a fixed mint over both themes. */}
            <linearGradient id="hero-arc-band-grad" gradientUnits="userSpaceOnUse" x1="0" y1="-32" x2="0" y2="104">
              {/* Light→deep mint, tuned so every band centreline (where the
                  label text sits) stays dark enough for the white labels to
                  read: the top WISH LIST band lands near the light stop, so the
                  light stop is a moderate mint, not a wash. */}
              <stop offset="0" stopColor="rgba(20, 180, 128, 0.95)" />
              <stop offset="0.55" stopColor="rgba(11, 150, 104, 0.95)" />
              <stop offset="1" stopColor="rgba(6, 96, 66, 0.96)" />
            </linearGradient>
          </defs>

          {/* Background arc strokes — the visible "chip" surface each label
              sits on. Each ring is a thick rounded stroke along its arc path.

              E51: all three bands share the slightly-transparent mint
              ARC_BAND_FILL (rgba(13,163,113,0.18)). They all read as one mint
              chip family with a faint see-through tint over both themes; the
              LIVE SCAN band stays the primary action via its stronger accent
              outline (opacity 1 vs 0.6) and accent-coloured curved label, not
              via a different fill. The drop-shadow is kept. */}
          {[
            ARC_R_WISHLIST, // WISH LIST (outermost)
            ARC_R_OUTER,    // NOTIFICATIONS
            ARC_R_MID,      // SETTINGS
            ARC_R_INNER,    // LIVE SCAN
          ].map((r) => (
            <path
              key={`arc-bg-${r}`}
              d={arcPath(ARC_CX, ARC_CY, r, ARC_SWEEP_DEG)}
              fill="none"
              stroke="url(#hero-arc-band-grad)"
              strokeWidth={STRIP * 2}
              strokeLinecap="round"
              style={{ filter: "drop-shadow(0 1px 3px rgba(0,0,0,0.18))" }}
            />
          ))}
          {/* E62: bevel edges replace the old white centreline hairline. The
              previous 1px white stroke ran along the band CENTRELINE — the same
              path the label text sits on — so it read as a line struck THROUGH
              every word. Depth now comes from a top rim-light and a bottom
              inner-shadow drawn at the band EDGES (offset ±(STRIP-2) from the
              centreline), well clear of the text: the outer/upper edge catches a
              soft white highlight, the inner/lower edge falls into shadow, so
              each band reads as a rounded, top-lit surface. Both are literal
              RGBA so they hold over light and dark themes (the mint fill is
              theme-fixed). */}
          {[ARC_R_WISHLIST, ARC_R_OUTER, ARC_R_MID, ARC_R_INNER].flatMap((r) => [
            <path
              key={`arc-rim-${r}`}
              d={arcPath(ARC_CX, ARC_CY, r + (STRIP - 2), ARC_SWEEP_DEG)}
              fill="none"
              stroke="rgba(255, 255, 255, 0.34)"
              strokeWidth={2}
              strokeLinecap="round"
            />,
            <path
              key={`arc-shadow-${r}`}
              d={arcPath(ARC_CX, ARC_CY, r - (STRIP - 2), ARC_SWEEP_DEG)}
              fill="none"
              stroke="rgba(0, 0, 0, 0.16)"
              strokeWidth={2}
              strokeLinecap="round"
            />,
          ])}

          {/* Curved labels along each arc. startOffset 50% + textAnchor middle
              centres the label on the top of the curve horizontally.

              E51: vertical centring fix for iOS WKWebView (the deploy target).
              WebKit mis-handles dominantBaseline:"central" on a <textPath>: it
              often falls back to baseline-on-path, so the glyph baseline (not its
              optical centre) lands on the arc centreline and the text floats
              toward the outer edge of the STRIP*2-thick band. The robust fix is
              two-fold: (1) set BOTH dominantBaseline AND alignmentBaseline to
              "central"/"middle" (WebKit honours alignmentBaseline on textPath
              where it ignores dominantBaseline), and (2) add a small downward dy
              (LABEL_BASELINE_DY) on the textPath as a font-metric correction so
              the glyph optical centre lands on the centreline even on engines
              that still resolve to the alphabetic baseline. The path is the
              band's centreline and the stroke is STRIP*2 thick, so glyph-centre-
              on-centreline = text vertically centred inside the band. Applied
              identically to all three labels. */}
          {/* LIVE SCAN label. Was split into a green "Live" + white "Scan"
              (E53) to mark the primary action, but --brand-green-on-light read
              as a dark, low-contrast word on the light theme, and the two-tspan
              run mis-centred on the arc on Android/Blink (WebKit centred the
              combined run; Blink did not). Collapsed to a single white run —
              identical to NOTIFICATIONS/SETTINGS — so it reads clearly in both
              themes AND centres on the arc on both engines. dy on the textPath
              (not tspans) applies the font-metric baseline correction the same
              way the other labels get it. */}
          <text
            dominantBaseline="central"
            alignmentBaseline="central"
            style={{
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              fill: "#FFFFFF",
            }}
          >
            <textPath
              href="#hero-arc-livescan"
              startOffset="50%"
              textAnchor="middle"
              dominantBaseline="central"
              alignmentBaseline="central"
              dy={LABEL_BASELINE_DY}
            >
              Live Scan
            </textPath>
          </text>
          {/* wish-list-screen: WISH LIST label on the outermost band. White fill,
              same font-metric dy correction as the other plain-text labels. */}
          <text
            dominantBaseline="central"
            alignmentBaseline="central"
            style={{
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              fill: "#FFFFFF",
            }}
          >
            <textPath
              href="#hero-arc-wishlist"
              startOffset="50%"
              textAnchor="middle"
              dominantBaseline="central"
              alignmentBaseline="central"
              dy={LABEL_BASELINE_DY}
            >
              Wish List
            </textPath>
          </text>
          {/* E53: NOTIFICATIONS label fill -> WHITE (was --text-primary). Literal
              #FFFFFF so it stays white on the fixed-mint band in both themes. */}
          <text
            dominantBaseline="central"
            alignmentBaseline="central"
            style={{
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              fill: "#FFFFFF",
            }}
          >
            <textPath
              href="#hero-arc-notifs"
              startOffset="50%"
              textAnchor="middle"
              dominantBaseline="central"
              alignmentBaseline="central"
              dy={LABEL_BASELINE_DY}
            >
              Notifications
            </textPath>
          </text>
          {/* E53: SETTINGS label fill -> WHITE (was --text-primary). Literal
              #FFFFFF so it stays white on the fixed-mint band in both themes. */}
          <text
            dominantBaseline="central"
            alignmentBaseline="central"
            style={{
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              fill: "#FFFFFF",
            }}
          >
            <textPath
              href="#hero-arc-settings"
              startOffset="50%"
              textAnchor="middle"
              dominantBaseline="central"
              alignmentBaseline="central"
              dy={LABEL_BASELINE_DY}
            >
              Settings
            </textPath>
          </text>

          {/* Invisible tap-target arcs — each one is a thick transparent stroke
              along its arc path that captures pointer events for that menu
              item. Rendered AFTER the labels so they sit on top and win the
              hit test. pointer-events: stroke confines hits to the stroked
              ring (not the bounding box), so the three items don't overlap. */}
          {/* PR-E12 fix 2: tap-target radii re-bound to match the new label
              order (NOTIFICATIONS outer, SETTINGS mid, LIVE SCAN inner). */}
          <path
            d={arcPath(ARC_CX, ARC_CY, ARC_R_WISHLIST, ARC_SWEEP_DEG)}
            fill="none"
            stroke="transparent"
            strokeWidth={STRIP * 2}
            strokeLinecap="round"
            style={{ pointerEvents: "stroke", cursor: "pointer" }}
            data-testid="hero-dex-action-wishlist"
            role="menuitem"
            aria-label="Wish List"
            onClick={() => {
              onClose();
              onWishList();
            }}
          />
          <path
            d={arcPath(ARC_CX, ARC_CY, ARC_R_OUTER, ARC_SWEEP_DEG)}
            fill="none"
            stroke="transparent"
            strokeWidth={STRIP * 2}
            strokeLinecap="round"
            style={{ pointerEvents: "stroke", cursor: "pointer" }}
            data-testid="hero-dex-action-notifications"
            role="menuitem"
            aria-label="Notifications"
            onClick={() => {
              onClose();
              onNotifications();
            }}
          />
          <path
            d={arcPath(ARC_CX, ARC_CY, ARC_R_MID, ARC_SWEEP_DEG)}
            fill="none"
            stroke="transparent"
            strokeWidth={STRIP * 2}
            strokeLinecap="round"
            style={{ pointerEvents: "stroke", cursor: "pointer" }}
            data-testid="hero-dex-action-settings"
            role="menuitem"
            aria-label="Settings"
            onClick={() => {
              onClose();
              onSettings();
            }}
          />
          <path
            d={arcPath(ARC_CX, ARC_CY, ARC_R_INNER, ARC_SWEEP_DEG)}
            fill="none"
            stroke="transparent"
            strokeWidth={STRIP * 2}
            strokeLinecap="round"
            style={{ pointerEvents: "stroke", cursor: "pointer" }}
            data-testid="hero-dex-action-livescan"
            role="menuitem"
            aria-label="Live Scan"
            onClick={() => {
              onClose();
              onLiveScan();
            }}
          />
        </svg>
      </div>
    </>
  );
}

export function HeroDexButton({ href, active }: HeroDexButtonProps) {
  const [location, setLocation] = useLocation();
  const [panelOpen, setPanelOpen] = useState(false);
  // PR-E8 fix 6: read tier so the wifi-arc menu can crown the stack with a
  // tier-aware "Pro" / "Basic" pill. tierLabel() handles the trial / unlocked
  // / ultimate variants too — we keep the full label so a Trial user sees
  // "Trial" rather than being mis-pilled as "Basic".
  const { tier, legalOpen, trainerStepOpen, tourOpen, onboardingOpen, nexQuestionnaireOpen, paywallOpen } = useSession();
  const isPro = hasProAccess(tier);
  const tierName = tierLabel(tier);
  // b17i C: the coachmark. Since 2026-10-23 it waits its turn with the other
  // first-run hints (lib/firstRunPrompts): from the second session, never in
  // a session that already had an ask, and not while something covers the
  // bar. It used to fire on the first mount, under the tour overlay on day
  // one, and dismiss itself unseen four seconds later.
  const coachmarkDue = useFirstRunPromptDue("coachmark");
  const barCovered = Boolean(
    legalOpen || trainerStepOpen || tourOpen || onboardingOpen || nexQuestionnaireOpen || paywallOpen,
  );
  const [showCoachmark, setShowCoachmark] = useState(false);
  // Tracks whether the current pointer interaction has already fired the
  // long-press, so the trailing click/tap doesn't ALSO navigate.
  const longPressedRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // b17m E: quick-start hook — starts live scan immediately on chip tap. The
  // scan variant is owned by the LiveScanProvider singleton, which subscribes to
  // the last-active dex pill (b17r R2) so the scan runs for the dex the user is
  // actually viewing — reactively, not a cold-launch "pokemon" snapshot. No
  // variant is threaded in here any more.
  const { quickStart } = useDexLiveScanQuickStart();

  useEffect(() => {
    if (!coachmarkDue || barCovered) return;
    claimFirstRunPrompt("coachmark");
    setShowCoachmark(true);
  }, [coachmarkDue, barCovered]);

  const dismissCoachmark = useCallback(() => {
    setShowCoachmark(false);
    markFirstRunPromptDone("coachmark");
  }, []);

  const clearTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const openPanel = () => {
    longPressedRef.current = true;
    setPanelOpen(true);
    void hapticMedium();
  };

  const onPointerDown = () => {
    longPressedRef.current = false;
    clearTimer();
    timerRef.current = setTimeout(openPanel, LONG_PRESS_MS);
  };
  const onPointerUpOrLeave = () => clearTimer();

  const handleLiveScan = useCallback(() => {
    void hapticTick();
    void quickStart();
  }, [quickStart]);

  // PR-E8 fix 6: new wifi-arc menu items — open the notifications inbox via
  // the cross-component custom event (AppShell owns the sheet state) and
  // navigate to Settings via wouter. Both fire a haptic tick to match the
  // Live Scan affordance.
  const handleNotifications = useCallback(() => {
    void hapticTick();
    window.dispatchEvent(new CustomEvent(INBOX_OPEN_EVENT));
  }, []);
  const handleSettings = useCallback(() => {
    void hapticTick();
    if (!confirmPageExit()) return;
    setLocation("/settings");
  }, [setLocation]);
  // wish-list-screen: the 4th arc navigates to the Wish List screen.
  const handleWishList = useCallback(() => {
    void hapticTick();
    if (!confirmPageExit()) return;
    setLocation("/wish-list");
  }, [setLocation]);

  const onClick = () => {
    clearTimer();
    // A long-press already handled the interaction — ignore the trailing click.
    if (longPressedRef.current) {
      longPressedRef.current = false;
      return;
    }
    // REMOVED 2026-08-11 (was b17p C2): "already on the dex → a single press
    // starts the live scan". Starting a screen recording is far too heavy an
    // action to hang off an undifferentiated tap on the app's largest, most
    // central control — the same tap that is a plain navigation everywhere else.
    // The scan is still one deliberate gesture away: long-press opens the menu
    // and its LIVE SCAN chip calls handleLiveScan, which is the affordance the
    // coachmark already teaches.
    if (!confirmPageExit()) return;
    setLocation(href);
  };

  // Close on Escape for keyboard/desktop parity.
  useEffect(() => {
    if (!panelOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setPanelOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [panelOpen]);

  useEffect(() => clearTimer, []);

  const onDex = location.startsWith("/dex");
  const ariaLabel = onDex
    ? "Nexus Dex live scan: tap to start, long-press for menu"
    : "Nexus Dex: tap to open, long-press for quick actions";

  return (
    // b17m C → b17p C1: was marginTop -2px; lowered another 30px to 28px so the
    // medallion and tab bar sit lower on tall devices.
    // Sibling constant HERO_TOP_OFFSET_PX must match. The flanking columns in
    // TabBar apply the same value as paddingTop so all three columns share the
    // same visual midline.
    <div
      className="relative flex flex-col items-center justify-end px-1 select-none"
      style={{ marginTop: `${HERO_TOP_OFFSET_PX}px`, height: `${HERO_WRAPPER_HEIGHT_PX}px` }}
    >
      {/* PR-E8 fix 6: wifi-arc QuickActionMenu. Long-press the medallion to
          reveal three stacked label arcs (LIVE SCAN → NOTIFICATIONS →
          SETTINGS) crowned by the tier pill. The arcs sit ABOVE the medallion
          so the chrome no longer overlaps the coin itself. */}
      {panelOpen && (
        <QuickActionMenu
          onClose={() => setPanelOpen(false)}
          onLiveScan={handleLiveScan}
          onNotifications={handleNotifications}
          onSettings={handleSettings}
          onWishList={handleWishList}
          tierName={tierName}
          isPro={isPro}
        />
      )}

      {/* b17i C: first-launch coachmark. Anchored above the medallion. */}
      {showCoachmark && (
        <MedallionCoachmark onDismiss={dismissCoachmark} />
      )}

      <button
        type="button"
        data-testid="tab-dex"
        data-active={active || undefined}
        data-revealed={panelOpen || undefined}
        aria-label={ariaLabel}
        aria-haspopup="menu"
        aria-expanded={panelOpen}
        onClick={onClick}
        onPointerDown={onPointerDown}
        onPointerUp={onPointerUpOrLeave}
        onPointerLeave={onPointerUpOrLeave}
        onContextMenu={(e) => e.preventDefault()}
        className="compass-fab group relative flex items-center justify-center"
      >
        {/* b17m A+B: medallion uses nexus_dex_medallion_3d.png — the full 3D
            coin with silver bezel + green inner + N logo baked in. b17q Q2: the
            __glow halo is removed entirely. b17q Q3: the emblem fills the coin
            edge-to-edge (was 112px, which exposed a 16px chrome ring). b17y-ui:
            coin shrunk 128 to 120 (~6%) after visual review. E35: coin shrunk a
            further 20% (120 -> 96) via COIN_DIAMETER_PX in tabbarLayout so the
            two inner tabs clear its footprint. */}
        <span
          className="compass-coin"
          data-active={active ? "true" : undefined}
          style={{ width: COIN_DIAMETER_PX, height: COIN_DIAMETER_PX }}
          aria-hidden
        >
          {/* b17m B: no __face / __ring-inner / __disc wrappers.
              The 3D medallion image IS the full coin including bezel + disc. */}
          <img
            src={nexusDexMedallion}
            alt="Nexus Dex"
            width={COIN_DIAMETER_PX}
            height={COIN_DIAMETER_PX}
            draggable={false}
            className="compass-coin__emblem"
            style={{
              pointerEvents: "none",
              userSelect: "none",
              WebkitUserSelect: "none",
              WebkitTouchCallout: "none",
              // WebKitUserDrag isn't in React's CSSProperties; drag is
              // already disabled via draggable={false} and the
              // .compass-coin__emblem class (-webkit-user-drag: none).
            }}
          />
        </span>
      </button>
    </div>
  );
}
